/**
 * Notification Service
 * 整合本地通知和遠端推播的統一入口
 */
import { Capacitor } from '@capacitor/core'
import type { VaccinationRecord, NotificationTiming } from '../types'
import { getDisplayPlannedDate } from '../utils/vaccineDate'

// Re-export local notification functions
export {
  requestLocalNotificationPermission,
  checkLocalNotificationPermission,
  scheduleVaccineReminder,
  cancelAllLocalNotifications,
  rescheduleAllVaccineReminders,
  testLocalNotification,
  addLocalNotificationListeners,
  calculateNotificationTime,
} from './localNotificationService'

// Re-export push notification functions
export {
  requestPushPermission,
  checkPushPermission,
  registerForPush,
  addPushListeners,
  removeAllPushListeners,
  initializePushNotifications,
} from './pushNotificationService'

/**
 * 請求通知權限（統一入口）
 * 會同時請求本地通知和遠端推播權限
 */
export async function requestNotificationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (Capacitor.isNativePlatform()) {
    // 原生平台使用 Capacitor API
    const { requestLocalNotificationPermission } = await import('./localNotificationService')
    return await requestLocalNotificationPermission()
  } else {
    // Web 環境使用 Web Notification API（fallback）
    if (!('Notification' in window)) {
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    const permission = await Notification.requestPermission()
    return permission === 'granted' ? 'granted' : permission === 'denied' ? 'denied' : 'prompt'
  }
}

/**
 * 檢查通知權限狀態
 */
export async function getNotificationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (Capacitor.isNativePlatform()) {
    const { checkLocalNotificationPermission } = await import('./localNotificationService')
    return await checkLocalNotificationPermission()
  } else {
    if (!('Notification' in window)) {
      return 'denied'
    }
    return Notification.permission === 'granted' ? 'granted'
      : Notification.permission === 'denied' ? 'denied'
        : 'prompt'
  }
}

/**
 * 檢查是否支援推播
 */
export function isNotificationSupported(): boolean {
  if (Capacitor.isNativePlatform()) {
    return true
  }
  return 'Notification' in window
}

/**
 * 重新排程所有通知（統一入口）
 */
export async function rescheduleAllNotifications(
  records: VaccinationRecord[],
  getBabyName: (babyId: string) => string,
  getVaccineName: (vaccineId: string) => string,
  getDoseLabel: (doseId: string) => string,
  timings: NotificationTiming[]
): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { rescheduleAllVaccineReminders } = await import('./localNotificationService')
    await rescheduleAllVaccineReminders(records, getBabyName, getVaccineName, getDoseLabel, timings)
  } else {
    // Web 環境的 fallback 實作
    await rescheduleWebNotifications(records, getBabyName, getVaccineName, getDoseLabel, timings)
  }
}

/**
 * 取消所有通知（統一入口）
 */
export async function cancelAllNotifications(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { cancelAllLocalNotifications } = await import('./localNotificationService')
    await cancelAllLocalNotifications()
  } else {
    localStorage.removeItem('scheduled_notifications')
  }
}

/**
 * 測試推播（統一入口）
 */
export async function testNotification(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { testLocalNotification } = await import('./localNotificationService')
    await testLocalNotification()
  } else {
    testWebNotification()
  }
}

// ==================== Web 環境 Fallback ====================

/**
 * Web 環境的排程邏輯（使用 setTimeout）
 */
async function rescheduleWebNotifications(
  records: VaccinationRecord[],
  getBabyName: (babyId: string) => string,
  getVaccineName: (vaccineId: string) => string,
  getDoseLabel: (doseId: string) => string,
  timings: NotificationTiming[]
): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return
  }

  // 清除舊的排程
  localStorage.removeItem('scheduled_notifications')

  // 只為計劃中的紀錄排程
  const activeRecords = records.filter(r => r.status === 'planned')

  if (activeRecords.length === 0) {
    return
  }

  // 找出最近即將到來的一個疫苗
  const sortedRecords = [...activeRecords].sort((a, b) => {
    return new Date(getDisplayPlannedDate(a)).getTime() - new Date(getDisplayPlannedDate(b)).getTime()
  })

  const nextRecord = sortedRecords[0]
  if (!nextRecord) return

  const babyName = getBabyName(nextRecord.babyId)
  const vaccineName = getVaccineName(nextRecord.vaccineId)
  const doseLabel = getDoseLabel(nextRecord.doseId)

  const { calculateNotificationTime } = await import('./localNotificationService')

  for (const timing of timings) {
    const notifyAt = calculateNotificationTime(getDisplayPlannedDate(nextRecord), timing)

    if (notifyAt && notifyAt > new Date()) {
      const delay = notifyAt.getTime() - Date.now()

      if (delay > 5 * 60 * 1000) { // 至少 5 分鐘後
        setTimeout(() => {
          showWebNotification(babyName, vaccineName, doseLabel)
        }, delay)
      }
    }
  }
}

/**
 * 顯示 Web 通知
 */
function showWebNotification(babyName: string, vaccineName: string, doseLabel: string): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return
  }

  const title = `${babyName} 快需要打疫苗了`
  const body = `${vaccineName} ${doseLabel}，記得跟醫院診所預約`

  new Notification(title, {
    body,
    icon: '/icon.png',
  })
}

/**
 * 測試 Web 通知
 */
function testWebNotification(): void {
  if (!('Notification' in window)) {
    alert('此瀏覽器不支援推播通知')
    return
  }

  if (Notification.permission !== 'granted') {
    alert('請先允許推播通知權限')
    return
  }

  new Notification('測試推播', {
    body: '如果你看到這個，表示推播功能正常！',
  })
}

// ==================== FCM 相關（保留給未來使用）====================

/**
 * 取得 FCM Token（透過 Capacitor Push Notifications）
 */
export async function getFCMToken(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) {
    return null
  }

  // FCM Token 會在 PushNotifications.addListener('registration') 中取得
  // 這裡只是一個 placeholder
  return null
}
